import React, { useState, useEffect } from 'react';
import { useSteps } from '../../../hooks/useSteps';
import BackForwardButtons from '../../../components/buttons/BackForwardButtons';
import { Helmet } from 'react-helmet';
import { DropdownButtonComponent as DropdownButton } from '../../../components';
import RentRow from './RentRow';
import { KeyMetrics } from '../../../components';
import { FORMS } from '../../../schemas/stepMap';
import { Link, useNavigate } from 'react-router-dom';
import { getLift } from './helpers';
import { useProperty } from '../../../hooks/useProperty';

export default function RentRoll() {
  //for large results, we can paginate the display (temporarily disabled, will see if we need it)
  const [slice, setSlice] = useState([0, 20]);

  //for the dropdowns, we can expand/collapse all or only one based on the index
  const [expanded, setExpanded] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  //navigation from the context provider
  const { prev, next } = useSteps();

  const navigate = useNavigate();
  //data from the context provider
  const { propertyData, scenarios } = useProperty();

  if (propertyData?.projectType === 'flip') {
    navigate('../flip-analysis');
  }

  let { grossIncomePerUnit, rentalIncomePreVacancy, enterTotalRentalIncome } =
    propertyData || {};
  const scenarioData = scenarios.find((scenario) => !scenario.isDefault);

  //add the updated rent as a new field to the original rent roll data
  scenarioData?.grossIncomePerUnit?.map((unit, index) => {
    if (grossIncomePerUnit && grossIncomePerUnit[index]) {
      grossIncomePerUnit[index].marketRent = unit.rent;
    }
  });

  //if we were to paginate the results, we would slice the data here
  const unitData = grossIncomePerUnit;

  //extra units from the BRRRR scenario, if any
  const newUnits =
    scenarioData?.grossIncomePerUnit?.length -
      (grossIncomePerUnit?.length || 0) <
    1
      ? null
      : scenarioData?.grossIncomePerUnit?.slice(
          grossIncomePerUnit?.length || 0
        );

  if (
    ((!unitData?.length || !Array.isArray(unitData)) && !newUnits) ||
    enterTotalRentalIncome
  ) {
    return (
      <div className="w-full flex flex-col items-center justify-center mt-36">
        <h2 className="mb-6 text-xl font-medium text-primary">
          No Units Created
        </h2>
        <p className="mb-72 w-2/3 text-center">
          Once you add unit details in the{' '}
          <Link
            to="../purchase-worksheet"
            className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
          >
            purchase worksheet
          </Link>
          , you will be able to view your rent roll details here.
        </p>
      </div>
    );
  }

  //data for the key metrics, which are displayed at the top of the page
  const metricsData = {
    units: grossIncomePerUnit?.length,
    rentalIncomePreVacancy,
    marketRent: scenarioData?.rentalIncomePreVacancy,
    lift: getLift(rentalIncomePreVacancy, scenarioData?.rentalIncomePreVacancy),
    occupied: grossIncomePerUnit
      ? `${grossIncomePerUnit.reduce(
          (acc, unit) => acc + (unit.isOccupied === true ? 1 : 0),
          0
        )} / ${grossIncomePerUnit?.length}`
      : '-',
  };

  // data for the totals row
  const totalsData = {
    securityDeposit: grossIncomePerUnit?.reduce(
      (acc, unit) => acc + (unit.securityDeposit || 0),
      0
    ),
    numberOfBedrooms: grossIncomePerUnit?.reduce(
      (acc, unit) => acc + (unit.numberOfBedrooms || 0),
      0
    ),
    numberOfBathrooms: grossIncomePerUnit?.reduce(
      (acc, unit) => acc + (unit.numberOfBathrooms || 0),
      0
    ),
    rent: { value: rentalIncomePreVacancy, unit: 'monthly' },
    marketRent: {
      value: scenarioData?.rentalIncomePreVacancy,
      unit: 'monthly',
    },
  };

  //schema for the key metrics, for now the calculations are done in this component instead of the form builder
  const keySchema = FORMS.purchaseAnalysis.rentRollMetrics;

  const closeAll = () => {
    setExpanded(
      grossIncomePerUnit?.reduce((acc, _, index) => {
        acc[index] = false;
        return acc;
      }, {})
    );
  };

  const expandAll = () => {
    setExpanded(
      grossIncomePerUnit?.reduce((acc, _, index) => {
        acc[index] = true;
        return acc;
      }, {})
    );
  };

  const toggleAll = () => {
    allExpanded ? closeAll() : expandAll();
    setAllExpanded(!allExpanded);
  };

  //close all dropdowns on mount
  useEffect(() => {
    grossIncomePerUnit && closeAll();
  }, []);

  //toggle a single dropdown
  const toggleOne = (index) => {
    setExpanded((prev) => {
      return {
        ...prev,
        [index]: !prev[index],
      };
    });
  };

  return (
    <>
      <Helmet>
        <title>PropertyCast.io - Rent Roll</title>
      </Helmet>
      <section id="rent-roll -mx-2">
        <div className="pt-6">
          <KeyMetrics schema={keySchema} data={metricsData} classes="py-3" />
        </div>
        <div className="grid grid-cols-5rentroll sm:grid-cols-9rentroll space-y-2 gap-2 px-2 mt-8 pt-3 pb-4 text-base text-center font-semibold text-neutral-600  overflow-x-auto">
          <div className="grid grid-cols-5rentroll sm:grid-cols-9rentroll col-span-8 sm:col-span-12 -mx-1 py-2 overflow-x-auto">
            <div className="col-span-3 sm:col-span-7 pl-6 text-left text-white pb-4 mb-3 border-b-2">
              <span className="  bg-neutral-400 rounded p-1">On Purchase</span>
            </div>
            <div className="col-span-3 pl-6 text-left  text-white pb-4 mb-3 border-b-2 border-primary">
              <span className=" bg-primary rounded p-1">Market Rent</span>
            </div>
            <span>{''}</span>
            <div className="px-1">
              <h2>Unit</h2>
            </div>
            <div className="px-1 hidden sm:inline ">
              <h2>Tenant</h2>
            </div>
            <div className="px-1 hidden sm:inline ">
              <h2>Bed / Bath</h2>
            </div>
            <div className="hidden sm:inline px-1">
              <h2>Lease Start</h2>
            </div>
            <div className="hidden sm:inline px-1">
              <h2>Deposit</h2>
            </div>
            <div className="px-1">
              <h2>Rent</h2>
            </div>
            <div className="px-1">
              <h2>Market Rent</h2>
            </div>
            <div className="px-1">
              <h2>Lift</h2>
            </div>
            <div className=" flex justify-end sm:pr-2 pl-1">
              <span className="-mt-1">
                <DropdownButton setOpen={toggleAll} isOpen={allExpanded} />
              </span>
            </div>
          </div>
          {unitData &&
            unitData.map((unit, index) => {
              return index < unitData.length - 1 ? (
                <RentRow
                  key={unit.unitName + index}
                  unit={unit}
                  unitNum={index + slice[0] + 1}
                  isOpen={expanded[index + slice[0]]}
                  setOpen={() => toggleOne(index + slice[0])}
                />
              ) : (
                <React.Fragment key={'totals'}>
                  <RentRow
                    key={unit.unitName + index}
                    unit={unit}
                    unitNum={index + slice[0] + 1}
                    isOpen={expanded[index + slice[0]]}
                    setOpen={() => toggleOne(index + slice[0])}
                  />
                </React.Fragment>
              );
            })}

          {newUnits &&
            newUnits.map((unit, index2) => {
              return (
                <React.Fragment key={unit.unitName + index2 + 'brrr'}>
                  {index2 === 0 && (
                    <div className="w-full col-span-6 lg:col-span-10">
                      Proposed Units
                    </div>
                  )}
                  <RentRow
                    key={unit.unitName + index2 + 'brrrr'}
                    unit={{
                      ...unit,
                      marketRent: unit.rent,
                      rent: null,
                      securityDeposit: null,
                      leaseStart: null,
                      tenantNames: null,
                      numberOfBedrooms: null,
                      numberOfBathrooms: null,
                      newUnit: true,
                    }}
                    unitNum={
                      (grossIncomePerUnit?.length || 0) + index2 + slice[0] + 2
                    }
                  />
                </React.Fragment>
              );
            })}

          <RentRow
            unit={{ unitName: 'Totals', ...totalsData }}
            unitNum={'Totals'}
            isOpen={false}
          />
        </div>

        <BackForwardButtons prev={prev} next={next} />
      </section>
    </>
  );
}
